import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./index.pc.module.scss";
import ChatBtn from "./chat.pc";
import Image from "next/image";
import classNames from "classnames";
import { getLang } from "helpers/country";
import { Tooltip } from "antd";
import { setJobViewCookiesTracker, viewJobSourceTracker } from "components/ReportTracker/util";
import { setCookie, getCookie } from "helpers/cookies";

const JobTags = ({ featureTags, normalTags, maxRow }) => {
  const styledFeatureTags = useMemo(() => {
    const colors = ["#E4FDFF", "#E8F3FF"];
    const getColor = () => {
      return colors[Math.random() > 0.5 ? 1 : 0];
    };

    return (featureTags || [])?.slice(0, 3)?.map((item) => {
      return {
        tag: item,
        style: { background: getColor() }
      };
    });
  }, [featureTags]);

  let totalTags = [].concat(styledFeatureTags, normalTags);

  if (!totalTags.length) {
    return null;
  }

  return (
    <div style={{ paddingTop: 4 }}>
      <div
        className={styles.listTag}
        style={{ maxHeight: maxRow * 24 + Math.max((maxRow - 1) * 8, 0) }}
      >
        {normalTags?.length > 0
          ? totalTags.map((item, index) => {
              let tag = item.tag || item;
              let style = item.style || null;
              return (
                <span className={styles.jobCardLocationItem} style={style} key={index}>
                  {tag}
                </span>
              );
            })
          : null}
      </div>
    </div>
  );
};

const JobCardPc = ({
  item,
  index,
  currentIndex,
  changeDetail,
  showChatBtn = false,
  cardType = "column",
  blur = false,
  source = undefined
}) => {
  const lang = getLang();
  const {
    company = {},
    salary_info = {},
    job_tags = {},
    recruiter = {},
    card_tags = []
  } = item || {};
  const featureTags = card_tags;

  const { reply_label = "", reply_label_number = "" } = recruiter;
  const displayInRow = cardType === "row";
  const titleRef = useRef<HTMLHeadingElement>();
  const [visible, setVisible] = useState(false);
  const [moveIn, setMoveIn] = useState(false);

  let reply = reply_label;
  if (reply_label_number) {
    reply = reply?.replace(String(reply_label_number), `<span>${reply_label_number}</span>`);
  }

  const checkTextOverflow = () => {
    if (titleRef.current && cardType === "row") {
      const isTruncated = titleRef.current.scrollWidth > titleRef.current.clientWidth;
      setVisible(isTruncated);
      return;
    }

    setVisible(false);
  };

  useEffect(() => {
    checkTextOverflow();
  }, [item.job_title, cardType]);

  const handleJobCardTracker = () => {
    if (!source) return;

    setJobViewCookiesTracker(setCookie, {
      source,
      reco_from: item?.reco_from ? item?.reco_from : null,
      query_id: item?.query_id ? item?.query_id : null
    });
  };

  const handleOnClick = (e, index) => {
    changeDetail && changeDetail(e, index);
    if (item.job_url && cardType === "row") {
      handleJobCardTracker();
      window.open(`/${lang}${item.job_url}`, "_blank");
    }
  };

  return (
    <div
      key={item.job_id}
      className={classNames(
        {
          [styles.listItem]: true,
          [styles.listItemActive]: index === currentIndex && cardType === "column",
          [styles.listItemChat]: showChatBtn,
          [styles.listItemBlur]: blur
        },
        "yolo-technology-jobCard"
      )}
      onClick={(e) => handleOnClick(e, index)}
      onMouseOver={() => !blur && setMoveIn(true)}
      onMouseOut={() => !blur && setMoveIn(false)}
    >
      <div className={styles.wrapper}>
        <div className={styles.jobHireTop}>
          <h3
            className={classNames({
              [styles.jobHireTopTitle]: true,
              [styles.jobHireTopTitleAndCardColumn]: cardType === "row"
            })}
          >
            {visible ? (
              <Tooltip title={item.job_title}>
                <span ref={titleRef}>{item.job_title}</span>
              </Tooltip>
            ) : (
              <span ref={titleRef}>{item.job_title}</span>
            )}

            {item?.is_urgent && (
              <span className={styles.jobHireTitleUrgent}>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='15'
                  height='14'
                  viewBox='0 0 15 14'
                  fill='none'
                >
                  <path
                    d='M10.2416 1.75098H6.0334C5.83777 1.75098 5.6605 1.86618 5.58105 2.04494L2.97486 7.90887C2.82938 8.23621 3.06899 8.60492 3.4272 8.60492H6.09248L4.78982 13.1642C4.73302 13.363 4.98066 13.5049 5.12343 13.3553L11.7669 6.39552C12.0676 6.08051 11.8443 5.55872 11.4088 5.55872H9.13868L10.6844 2.46736C10.8489 2.13823 10.6096 1.75098 10.2416 1.75098Z'
                    fill='black'
                  />
                </svg>
                {item?.urgent_text}
              </span>
            )}
          </h3>

          <div className={styles.container}>
            <div className={styles.salary}>
              <span className={styles.salaryText}>
                {salary_info?.text}
                {salary_info?.type_text && (
                  <span className={styles.month}>[{salary_info?.type_text}]</span>
                )}
              </span>
            </div>
            <div className={styles.chat}>
              {showChatBtn && (
                <ChatBtn chat_button={item.chat_button} jobId={item.job_id} data={item} />
              )}
            </div>
          </div>

          {/* <div className={styles.company_name}>{company?.company_name}</div> */}

          <JobTags maxRow={displayInRow ? 1 : 2} featureTags={featureTags} normalTags={job_tags} />
        </div>
        {(moveIn && cardType === "column" && index !== currentIndex) ||
        (moveIn && cardType === "row") ? (
          <div className={styles.listCompany}>
            <div
              className={classNames({
                [styles.listCompanyLogo]: true,
                [styles.listCompanyLogoActive]: recruiter?.is_online
              })}
            >
              <Image src={recruiter?.avatar} alt={recruiter?.company_name} width={32} height={32} />
            </div>

            <a className={styles.jobHireCompanyName} href={company?.company_url} target='_blank'>
              <span className={styles.jobHireCompanyNameFullName}> {recruiter?.full_name} </span>
              <b>{recruiter?.job_title ? " · " : ""}</b>
              <span>{recruiter?.job_title ? recruiter?.job_title : ""}</span>
            </a>

            <span
              className={styles.listCompanyReply}
              dangerouslySetInnerHTML={{ __html: reply }}
            ></span>
          </div>
        ) : (
          <div className={classNames(styles.listCompany, styles.listCompanyRecruiter)}>
            <div
              className={classNames({
                [styles.listCompanyLogo]: true
              })}
            >
              <Image
                src={company?.company_logo}
                alt={company?.company_name}
                width={32}
                height={32}
              />
            </div>

            <p className={styles.jobHireRecruiterName}>{company?.company_name}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobCardPc;
